/**
 * Single source of truth for domain classification.
 *
 * Every check that asks "is this our domain?", "is this a customer custom
 * domain?", or "is this a dev machine?" should import from here — not
 * re-implement the logic inline.
 */

// ──────────────────────────────────────────────
// 1. Official Midus domains
// ──────────────────────────────────────────────

const MIDUS_ROOT_DOMAINS = ['heymidus.com', 'midus.co'];

const MIDUS_STATUS_HOSTNAMES = ['status.midus.co', 'status.localhost'];

function statusHostnameFromEnv() {
  const raw = process.env.REACT_APP_PUBLIC_STATUS_PAGE_URL;
  if (!raw) return '';
  try { return new URL(raw).hostname.toLowerCase(); }
  catch { return ''; }
}

function stripPort(hostname) {
  return String(hostname || '').toLowerCase().split(':')[0];
}

/** True when the hostname belongs to our root product domains. */
export function isRootDomain(hostname) {
  const h = stripPort(hostname ?? window.location.hostname);
  return MIDUS_ROOT_DOMAINS.some((d) => h === d || h.endsWith(`.${d}`));
}

/** True for our public status page hostname (fixed list + env). */
export function isStatusPageHost(hostname) {
  const h = stripPort(hostname ?? window.location.hostname);
  if (!h) return false;
  const fromEnv = statusHostnameFromEnv();
  if (fromEnv && h === fromEnv) return true;
  return MIDUS_STATUS_HOSTNAMES.includes(h);
}

// ──────────────────────────────────────────────
// 2. Local / development hosts
// ──────────────────────────────────────────────

const PRIVATE_IP_RE = /^(localhost|127\.|10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.)/;

/**
 * True for any hostname that is clearly a local dev environment —
 * localhost, loopback, private-range IPs, IPv6 loopback, or any
 * bare numeric IP.
 */
export function isLocalDevHost(hostname) {
  const raw = hostname ?? window.location.hostname;
  const h = stripPort(raw);
  if (!h) return false;
  if (h === 'localhost' || h.endsWith('.localhost')) return true;
  if (h === '[::1]') return true;
  if (PRIVATE_IP_RE.test(h)) return true;
  if (/^[\d.]+$/.test(h)) return true;
  return false;
}

// ──────────────────────────────────────────────
// 3. Custom domain detection (the one everyone needs)
// ──────────────────────────────────────────────

/**
 * True when the browser is running on a *customer* custom domain
 * (i.e. not our own domains, not our status site, and not a dev machine).
 */
export function isCustomDomain(hostname) {
  const h = stripPort(hostname ?? window.location.hostname);
  if (isLocalDevHost(h)) return false;
  if (isRootDomain(h)) return false;
  if (isStatusPageHost(h)) return false;
  return true;
}
